import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useState } from "react";
import { useStore } from "react-redux";
import { useNavigate } from "react-router-dom";

import { useAppDispatch } from "app/hooks";
import type { RootState } from "app/store";
import {
  setCustomMessage,
  setOpen
} from "components/action-snackbar/action-snackbar-slice";
import { AMBIENT_SCRIBE_PAUSED_FEATURE } from "constants/feature-flags";
import {
  fetchCurrentUser,
  setCurrentClinic,
  setCurrentRegion,
  setToken
} from "features/auth/auth-slice";
import { attachmentsCleared } from "features/calls/dialstack/call-attachments.slice";
import { popsCleared } from "features/calls/dialstack/incoming-call-pop.slice";
import { missedCallsCleared } from "features/calls/dialstack/missed-calls.slice";
import {
  RECORDING_STATE_IN_PROGRESS,
  RECORDING_STATE_SAVING,
  RECORDING_STATE_STOPPED
} from "features/medical-scribe/medical-scribe-constants";
import { selectMedicalScribeRecordingState } from "features/medical-scribe/medical-scribe.slice";
import useUserRoles from "hooks/use-user-roles.hook";
import { emptySplitApi } from "services/empty-split-api";
import { SwitchPracticeResponse } from "services/types/practices.type";
import { getAuthHeaderFromStorage } from "utils/auth-header.util";
import { viteEnv } from "utils/vite-env.util";

// Switched sessions are Bearer JWTs. We persist like the login flow so
// RequireAuth rehydrates the new session on reload.
const SWITCH_TOKEN_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const readAccessToken = (data: SwitchPracticeResponse): string | undefined =>
  data.access ?? data.token ?? data.auth_token;

// Persist the switched Bearer session into whichever bucket already holds the
// active token — matching `persistBearerAccessToken` (utils/services.util.ts)
// and the login flow. Writing unconditionally to localStorage would promote a
// session-only (sessionStorage) login into a persistent multi-day session AND
// orphan the old sessionStorage token. RequireAuth keys off the presence of a
// localStorage `expiration` to decide which bucket to rehydrate from, so we
// only write `expiration` for localStorage (keep-logged-in) sessions.
const persistSwitchedSession = (token: string): void => {
  const usesLocalStorage = localStorage.getItem("token") !== null;
  const storage = usesLocalStorage ? localStorage : sessionStorage;
  storage.setItem("token", token);
  storage.setItem("authScheme", "Bearer");
  if (usesLocalStorage) {
    // Fixed +7d, intentionally decoupled from the JWT `exp`: the access token's
    // own exp is short (~2h) and the 401→refresh path silently renews it, so
    // deriving `expiration` from `exp` would make a still-refreshable session
    // look expired. The true upper bound is the HttpOnly refresh cookie (JS
    // can't read it); a stale flag self-corrects on the next 401 when the
    // refresh fails and the user is logged out.
    storage.setItem("expiration", String(Date.now() + SWITCH_TOKEN_TTL_MS));
  }
};

/**
 * Why a Suno admin's switch is refused, worded for the state the recording is in (SN-20150).
 * Postpone keeps a paused visit for later and is offered only with its flag on.
 */
export const getSwitchBlockedMessage = (
  recordingState: number,
  isPostponeAvailable: boolean
): string => {
  if (recordingState === RECORDING_STATE_SAVING) {
    return "The Scribe recording is still saving. Switch practice once it has finished.";
  }
  if (!isPostponeAvailable) {
    return "Stop the Scribe recording before switching practice.";
  }
  return recordingState === RECORDING_STATE_IN_PROGRESS
    ? "Stop the Scribe recording, or pause it and click Postpone, before switching practice."
    : "Click Stop or Postpone on the paused Scribe recording before switching practice.";
};

/**
 * Drives a practice switch: mint a Bearer session, flip the auth scheme, drop
 * all practice-scoped state (clinic/region + the entire RTK Query cache),
 * repopulate the user for the new context, and navigate home.
 *
 * Uses a direct credentialed fetch (not RTK Query) because the switch response
 * sets the HttpOnly `refresh_token` cookie — `credentials: "include"` is
 * required for the browser to store it across the app-host/API-host split.
 *
 * No-op when switching into the already-active practice.
 *
 * Lands on the home route unless the caller passes `returnTo`, which the
 * practice header uses to keep a Suno admin inside Administration after a
 * switch started there. The destination's own route guards still apply.
 *
 * Refused for a Suno admin while a Scribe recording in this tab is live, paused or saving
 * (SN-20150). Their scribe WebSocket acts in the practice of the token it authenticates with, so
 * after a switch the next reconnect, Resume or Stop would look for the scribe in the new
 * practice, miss it, and discard the recording. Everyone else's WebSocket stays in their home
 * practice, so their switch is unchanged. A recording in another tab is not seen (follow-up).
 */
export default function useSwitchPractice() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const { isSunoAdmin } = useUserRoles();
  // Read on click, not subscribed to: four always-mounted callers would otherwise re-render on
  // every recording state change for a value only the click needs.
  const store = useStore<RootState>();
  const isPostponeAvailable =
    useFlags()[AMBIENT_SCRIBE_PAUSED_FEATURE] === true;

  const switchToPractice = useCallback(
    async (
      practiceId: number,
      currentPracticeId?: number,
      options?: { returnTo?: string }
    ) => {
      if (practiceId === currentPracticeId) return; // no-op: already active
      const recordingState = selectMedicalScribeRecordingState(
        store.getState()
      );
      if (isSunoAdmin && recordingState !== RECORDING_STATE_STOPPED) {
        dispatch(
          setCustomMessage(
            getSwitchBlockedMessage(recordingState, isPostponeAvailable)
          )
        );
        dispatch(setOpen(true));
        return;
      }
      setIsLoading(true);

      // Surface failures via the global snackbar so a failed switch is never
      // silent. (Callers rely on the snackbar, not a returned error value.)
      const fail = () => {
        const message = "Couldn't switch practice. Please try again.";
        dispatch(setCustomMessage(message));
        dispatch(setOpen(true));
      };

      try {
        const response = await fetch(
          `${viteEnv.VITE_SUNO_API}/auth/switch-practice/`,
          {
            method: "POST",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
              Authorization: getAuthHeaderFromStorage() ?? ""
            },
            body: JSON.stringify({ practice_id: practiceId })
          }
        );
        if (!response.ok) {
          fail();
          return;
        }
        const data: SwitchPracticeResponse = await response.json();
        const token = readAccessToken(data);
        if (!token) {
          fail();
          return;
        }

        // Persist + mirror the new Bearer session into the active storage
        // bucket (localStorage for keep-logged-in, sessionStorage otherwise).
        persistSwitchedSession(token);
        dispatch(setToken({ token, scheme: "Bearer" }));

        // The previous practice's clinic/region no longer apply; the boot init
        // effect re-selects a clinic for the new practice once clinics refetch.
        dispatch(
          setCurrentClinic({
            currentClinic: null,
            numberOfClinics: 0,
            hasRegions: false,
            hasServiceLocations: false
          })
        );
        dispatch(setCurrentRegion(null));

        // Drop every cached response — they were scoped to the old practice.
        dispatch(emptySplitApi.util.resetApiState());
        // Call ids and pops are practice-scoped too.
        dispatch(attachmentsCleared());
        dispatch(popsCleared());
        dispatch(missedCallsCleared());

        // Repopulate the user (practice, default_clinic, flags) for the new
        // context before landing on the home route.
        await dispatch(fetchCurrentUser({ token }));

        navigate(options?.returnTo ?? "/");
      } catch {
        fail();
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch, navigate, isSunoAdmin, isPostponeAvailable, store]
  );

  return { switchToPractice, isLoading };
}
